import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { AuthImg, TaskModal, PRIO_STYLE, SEV_STYLE, bLabel } from "./parts";
import { ArrowLeft, CheckCircle2, FileText, Plus, Loader2 } from "lucide-react";

export default function ManagerReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [taskDefaults, setTaskDefaults] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/scouting/visits/${id}`);
    setBundle(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!bundle) return <div className="p-8 font-bold uppercase text-sm">Loading…</div>;
  const { visit, blocks, observations, photos, tasks } = bundle;
  const blockOf = (rid) => blocks.find((b) => b.id === rid);

  const saveObs = async (o, upd) => {
    const body = { phenology: o.phenology, growth: o.growth, vigor: o.vigor, health: o.health, pests: o.pests,
      irrigation: o.irrigation, weeds: o.weeds, cultural: o.cultural, followups: o.followups,
      internal_comments: o.internal_comments, client_comments: o.client_comments,
      client_include: o.client_include, ...upd, done: false };
    await api.put(`/scouting/visits/${visit.id}/blocks/${o.ranch_id}`, body);
    setBundle((b) => ({ ...b, observations: b.observations.map((x) => (x.id === o.id ? { ...x, ...upd } : x)) }));
  };

  const updPhoto = async (pid, upd) => {
    const { data } = await api.put(`/scouting/photos/${pid}`, upd);
    setBundle((b) => ({ ...b, photos: b.photos.map((p) => (p.id === pid ? data : p)) }));
  };

  const updTaskPriority = async (t, priority) => {
    await api.put(`/tasks/${t.id}`, { ...t, priority });
    setBundle((b) => ({ ...b, tasks: b.tasks.map((x) => (x.id === t.id ? { ...x, priority } : x)) }));
  };

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/scouting/visits/${id}/status`, { status: "approved" }); await load(); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/scouting/visits/${id}/report/generate`);
      navigate(`/scouting/report/${data.id}`);
    } finally { setGenBusy(false); }
  };

  const approved = ["approved", "sent"].includes(visit.status);

  const summaryLine = (o) => {
    const bits = [];
    if (o.phenology?.stage) bits.push(o.phenology.stage);
    if (o.growth?.shoot_length_in != null) bits.push(`${o.growth.shoot_length_in}" shoots${o.growth.change_in != null ? ` (+${o.growth.change_in}")` : ""}`);
    if (o.vigor?.level) bits.push(`Vigor ${o.vigor.level}`);
    if (o.weeds?.pressure) bits.push(`Weeds ${o.weeds.pressure}`);
    return bits.join(" · ") || "No observations recorded";
  };

  return (
    <div className="space-y-5" data-testid="manager-review">
      <button onClick={() => navigate("/scouting")} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><ArrowLeft size={15} /> Scouting</button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Manager Review</h1>
          <div className="text-stone-500 text-sm mt-1">{[visit.client_name, visit.vineyard_name, visit.visit_date, `Scout: ${visit.scout_name}`].filter(Boolean).join(" · ")}
            <span className="ml-2 uppercase text-xs font-bold px-2 py-0.5 bg-[#1F3D34] text-white">{visit.status.replace("_", " ")}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {!approved && (
            <button data-testid="approve-visit-btn" onClick={approve} disabled={busy}
              className="h-12 px-5 bg-[#1F3D34] text-white font-bold uppercase text-sm flex items-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Approve
            </button>
          )}
          {approved && (
            <button data-testid="generate-report-btn" onClick={generate} disabled={genBusy}
              className="h-12 px-5 bg-[#7C9673] text-white font-bold uppercase text-sm flex items-center gap-2 disabled:opacity-40">
              {genBusy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} {genBusy ? "Generating…" : "Generate Client Report"}
            </button>
          )}
        </div>
      </div>

      {/* Observations per block */}
      {observations.map((o) => {
        const b = blockOf(o.ranch_id) || {};
        const oPhotos = photos.filter((p) => p.ranch_id === o.ranch_id);
        return (
          <div key={o.id} className={`bg-white border p-4 space-y-3 ${o.client_include ? "border-stone-200" : "border-stone-200 opacity-60"}`} data-testid={`review-obs-${o.id}`}>
            <div className="flex flex-wrap justify-between items-start gap-2">
              <div>
                <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{o.block_label ? bLabel(o.block_label) : b.name}</div>
                <div className="text-sm text-stone-500">{summaryLine(o)}</div>
              </div>
              <button onClick={() => saveObs(o, { client_include: !o.client_include })}
                className={`h-10 px-3 text-xs font-bold uppercase border ${o.client_include ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300 text-stone-500"}`}>
                {o.client_include ? "In Client Report" : "Excluded From Report"}
              </button>
            </div>

            {(o.pests?.length > 0 || o.health?.issues?.length > 0 || o.irrigation?.issues?.length > 0) && (
              <div className="flex flex-wrap gap-1.5 text-xs">
                {(o.health?.issues || []).map((i, k) => <span key={`h${k}`} className={`px-2 py-1 font-semibold ${SEV_STYLE[i.severity] || "bg-stone-200"}`}>{i.type}</span>)}
                {(o.pests || []).map((p, k) => <span key={`p${k}`} className={`px-2 py-1 font-semibold ${SEV_STYLE[p.severity] || "bg-stone-200"}`}>{p.name} · {p.severity}</span>)}
                {(o.irrigation?.issues || []).map((i, k) => <span key={`i${k}`} className="px-2 py-1 font-semibold bg-sky-100 text-sky-900">{i.type}{i.location ? ` @ ${i.location}` : ""}</span>)}
              </div>
            )}

            {o.internal_comments && <div className="text-xs bg-stone-50 border border-stone-200 p-2"><b className="uppercase text-[10px]">Internal (not shared):</b> {o.internal_comments}</div>}
            <div>
              <label className="text-xs font-bold uppercase text-stone-600 block mb-1">Client-Facing Comments (editable)</label>
              <textarea defaultValue={o.client_comments || ""} rows={2}
                onBlur={(e) => e.target.value !== (o.client_comments || "") && saveObs(o, { client_comments: e.target.value })}
                className="w-full border border-[#7C9673] p-2 text-sm" data-testid={`client-comments-${o.id}`} />
            </div>

            {oPhotos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {oPhotos.map((p) => (
                  <div key={p.id} className="border border-stone-200">
                    <AuthImg photoId={p.id} className="w-full h-20 object-cover" />
                    <input placeholder="Caption" defaultValue={p.caption || ""} onBlur={(e) => e.target.value !== (p.caption || "") && updPhoto(p.id, { caption: e.target.value })} className="w-full text-[10px] px-1 py-0.5 border-t border-stone-100" />
                    <button onClick={() => updPhoto(p.id, { client_eligible: true, selected_for_report: !p.selected_for_report })}
                      className={`w-full text-[9px] font-bold uppercase py-1 ${p.selected_for_report ? "bg-[#1F3D34] text-white" : p.client_eligible ? "bg-[#7C9673]/30" : "bg-stone-100 text-stone-500"}`}
                      data-testid={`photo-select-${p.id}`}>
                      {p.selected_for_report ? "★ In Report" : p.client_eligible ? "Select For Report" : "Internal — Tap To Use"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Tasks */}
      <div className="bg-white border border-stone-200 p-4 space-y-2">
        <div className="flex justify-between items-center">
          <span className="font-bold uppercase text-sm">Tasks From This Visit ({tasks.length})</span>
          <button onClick={() => setTaskDefaults({ client_id: visit.client_id, client_name: visit.client_name, ranch_number: visit.ranch_number, vineyard_name: visit.vineyard_name, visit_id: visit.id })}
            className="h-10 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white flex items-center gap-1"><Plus size={14} /> Add Task</button>
        </div>
        {tasks.map((t) => (
          <div key={t.id} className="flex flex-wrap justify-between items-center gap-2 border border-stone-100 p-2 text-sm">
            <div>
              <span className="font-semibold">{t.title}</span>
              <span className="text-xs text-stone-400 ml-2">{t.category} · {t.block_labels?.join(", ")}</span>
            </div>
            <select value={t.priority} onChange={(e) => updTaskPriority(t, e.target.value)} className={`h-9 px-2 text-xs font-bold uppercase ${PRIO_STYLE[t.priority]}`}>
              {["urgent", "high", "normal", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        ))}
      </div>

      <TaskModal open={!!taskDefaults} onClose={() => setTaskDefaults(null)} defaults={taskDefaults || {}} onCreated={load} />
    </div>
  );
}
